import { safeJsonParse } from './securityUtils';

export interface CartItem {
  id: string;
  name: string;
  price: string;
  image: string;
  category: string;
  duration?: string;
  quantity: number;
  planId?: string;
  planName?: string;
}

export interface WishlistItem {
  id: string;
  name: string;
  price: string;
  image: string;
  category: string;
  duration?: string;
  addedAt: string;
}

// Cart Functions
export const addToCart = (item: Omit<CartItem, 'quantity'>) => {
  const cart = getCartItems();
  const existingItem = cart.find(cartItem => cartItem.id === item.id && cartItem.planId === item.planId);
  
  if (existingItem) {
    existingItem.quantity += 1;
  } else {
    cart.push({ ...item, quantity: 1 });
  }
  
  localStorage.setItem('cart', JSON.stringify(cart));
  
  // Dispatch custom event to update header count
  window.dispatchEvent(new Event('cartUpdated'));
  
  return cart;
};

export const removeFromCart = (itemId: string, planId?: string) => {
  const cart = getCartItems();
  const updatedCart = cart.filter(item => 
    !(item.id === itemId && (planId ? item.planId === planId : true))
  );
  localStorage.setItem('cart', JSON.stringify(updatedCart));
  
  window.dispatchEvent(new Event('cartUpdated'));
  
  return updatedCart;
};

export const updateCartQuantity = (itemId: string, quantity: number, planId?: string) => {
  const cart = getCart();
  const item = cart.find(cartItem => 
    cartItem.id === itemId && (planId ? cartItem.planId === planId : true)
  );
  
  if (item) {
    if (quantity <= 0) {
      return removeFromCart(itemId, planId);
    }
    item.quantity = quantity;
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
  }
  
  return cart;
};

export const getCartItems = (): CartItem[] => {
  return safeJsonParse(localStorage.getItem('cart'), []);
};

// Backward compatibility alias
export const getCart = getCartItems;

export const clearCart = () => {
  localStorage.removeItem('cart');
  window.dispatchEvent(new Event('cartUpdated'));
};

export const getCartTotal = (): number => {
  const cart = getCartItems();
  return cart.reduce((total, item) => {
    const price = parseFloat(item.price.replace(/[^0-9.-]+/g, ""));
    return total + (price * item.quantity);
  }, 0);
};

export const getCartItemCount = (): number => {
  const cart = getCart();
  return cart.reduce((total, item) => total + item.quantity, 0);
};

// Wishlist Functions
export const addToWishlist = (item: Omit<WishlistItem, 'addedAt'>) => {
  const wishlist = getWishlistItems();
  const existingItem = wishlist.find(wishlistItem => wishlistItem.id === item.id);
  
  if (!existingItem) {
    wishlist.push({ ...item, addedAt: new Date().toISOString() });
    localStorage.setItem('wishlist', JSON.stringify(wishlist));
    window.dispatchEvent(new Event('wishlistUpdated'));
  }
  
  return wishlist;
};

export const removeFromWishlist = (itemId: string) => {
  const wishlist = getWishlistItems();
  const updatedWishlist = wishlist.filter(item => item.id !== itemId);
  localStorage.setItem('wishlist', JSON.stringify(updatedWishlist));
  
  window.dispatchEvent(new Event('wishlistUpdated'));
  
  return updatedWishlist;
};

export const getWishlistItems = (): WishlistItem[] => {
  return safeJsonParse(localStorage.getItem('wishlist'), []);
};

// Backward compatibility alias  
export const getWishlist = getWishlistItems;

export const isInWishlist = (itemId: string): boolean => {
  const wishlist = getWishlistItems();
  return wishlist.some(item => item.id === itemId);
};

export const clearWishlist = () => {
  localStorage.removeItem('wishlist');
  window.dispatchEvent(new Event('wishlistUpdated'));
};

export const getWishlistItemCount = (): number => {
  const wishlist = getWishlistItems();
  return wishlist.length;
};